/**
 * SkillDelivery Platform
 * InviteWelcome -- thin dispatcher (R14 Round 1 + Round 2 fix 1).
 *
 * ONE fetch per page load. The dispatcher pulls:
 *   - GET /public/invites/:token           (page JSON, stamps viewed_at)
 *   - GET /public/invites/:token/content   (admin-editable strings)
 *
 * and passes `{ initialState, initialContent }` to the chosen child.
 * Each child was previously doing its own duplicate GETs, which (a)
 * doubled the audit `CONNECT_PITCH_VIEWED` would-be-fire (prevented
 * server-side by `markViewedIfFirst`, but still wasteful) and (b)
 * burned a second network round-trip on every open. The props are
 * OPTIONAL so the children remain working stand-alone if ever mounted
 * directly in a test harness.
 *
 * Layout decision is server-side (data.page.layout from
 * INVITE_PAGE_LAYOUT) with preview-token `?layout=light` override.
 * Default -> classic -> no change to existing contacts.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import InviteWelcomeClassic, { API_BASE, Loading } from './InviteWelcome.classic';
import InviteWelcomeLight from './InviteWelcome.light';
import PreviewShell from '../components/invite/PreviewShell';

export default function InviteWelcome() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [content, setContent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/public/invites/${encodeURIComponent(token)}`,
          { cache: 'no-store' }
        );
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 410) {
          setState({ loading: false, expired: true, owner: body.owner || null });
          return;
        }
        if (res.status === 404 || !res.ok || !body || !body.success) {
          setState({ loading: false, notFound: true });
          return;
        }
        setState({
          loading: false,
          page: body.data.page,
          meta: body.data.meta
        });
      } catch (_) {
        if (!cancelled) setState({ loading: false, notFound: true });
      }
    })();
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/public/invites/${encodeURIComponent(token)}/content`,
          { headers: { Accept: 'application/json' } }
        );
        if (res.status === 204) return;
        const body = await res.json().catch(() => ({}));
        if (!cancelled && body && body.success) setContent(body.data || null);
      } catch (_) { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state.loading) {
    return <PreviewShell brand={{}} organisation=""><Loading /></PreviewShell>;
  }

  // Live-token layout decision from server. Expired / notFound keep
  // the classic shell so the "This invitation has expired" / "Not
  // found" branches render identically to today.
  const layout = (state.page && state.page.layout === 'light') ? 'light' : 'classic';

  if (layout === 'light') {
    return <InviteWelcomeLight initialState={state} initialContent={content} />;
  }
  return <InviteWelcomeClassic initialState={state} initialContent={content} />;
}
